import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"

export default class extends Controller {
    static values = {
        show: Boolean,
        updateUrl: String,
        dismissUrl: String,
        firstName: String,
        lastName: String,
        address: String,
        city: String,
        postal: String,
        countryId: String,
        provinceStateId: String,
        provinceOptions: Array
    }

    connect() {
        if (!this.showValue) return
        setTimeout(() => this.openPrompt(), 500)
    }

    provinceOptionsHtml() {
        return this.provinceOptionsValue.map(group => {
            const options = group.options.map(option => {
                const selected = String(option.id) === String(this.provinceStateIdValue) ? "selected" : ""
                return `<option value="${option.id}" ${selected}>${this.escape(option.name)}</option>`
            }).join("")

            return `<optgroup label="${this.escape(group.label)}">${options}</optgroup>`
        }).join("")
    }

    async openPrompt() {
        const result = await Swal.fire({
            title: "Please confirm your contact details",
            html: `
<div class="text-start mt-2">

  <div class="row g-2">

    <div class="col-6">
      <label class="form-label small mb-1">First name</label>
      <input id="swal-host-first-name" class="form-control form-control-sm"
        value="${this.escape(this.firstNameValue)}">
    </div>

    <div class="col-6">
      <label class="form-label small mb-1">Last name</label>
      <input id="swal-host-last-name" class="form-control form-control-sm"
        value="${this.escape(this.lastNameValue)}">
    </div>

    <div class="col-12">
      <label class="form-label small mb-1">Address</label>
      <input id="swal-host-address" class="form-control form-control-sm"
        value="${this.escape(this.addressValue)}">
    </div>

    <div class="col-6">
      <label class="form-label small mb-1">City</label>
      <input id="swal-host-city" class="form-control form-control-sm"
        value="${this.escape(this.cityValue)}">
    </div>

    <div class="col-6">
      <label class="form-label small mb-1">Province / State</label>
        <select id="swal-host-province-state-id" class="form-select form-select-sm">
          ${this.provinceOptionsHtml()}
        </select>
    </div>

    <div class="col-6">
      <label class="form-label small mb-1">Postal / ZIP</label>
      <input id="swal-host-postal" class="form-control form-control-sm"
        value="${this.escape(this.postalValue)}">
    </div>

    <div class="col-6">
      <label class="form-label small mb-1">Country</label>
      <select id="swal-host-country-id" class="form-select form-select-sm">
        <option value="2" ${String(this.countryIdValue) === "2" ? "selected" : ""}>Canada</option>
        <option value="1" ${String(this.countryIdValue) === "1" ? "selected" : ""}>United States</option>
      </select>
    </div>

  </div>

  <div class="form-check mt-3">
    <input class="form-check-input" type="checkbox" id="swal-host-confirm">
    <label class="form-check-label small">
      The information above is correct and up to date.
    </label>
  </div>

</div>
`,
            width: 560,
            confirmButtonText: "Save and continue",
            cancelButtonText: "Remind me later",
            showCancelButton: true,
            reverseButtons: true,
            allowOutsideClick: false,
            customClass: {
                popup: "rounded-4 p-3",
                confirmButton: "btn btn-primary px-4 py-2",
                cancelButton: "btn btn-light px-4 py-2 me-2"
            },
            buttonsStyling: false,
            preConfirm: async () => {
                const firstName = document.getElementById("swal-host-first-name")?.value.trim()
                const lastName  = document.getElementById("swal-host-last-name")?.value.trim()
                const address   = document.getElementById("swal-host-address")?.value.trim()
                const city      = document.getElementById("swal-host-city")?.value.trim()
                const provinceStateId = document.getElementById("swal-host-province-state-id")?.value
                const postal    = document.getElementById("swal-host-postal")?.value.trim()
                const countryId = document.getElementById("swal-host-country-id")?.value
                const confirmed = document.getElementById("swal-host-confirm")?.checked

                if (!firstName || !lastName || !address || !city || !provinceStateId || !postal || !countryId) {
                    Swal.showValidationMessage("Please complete all contact fields.")
                    return false
                }

                if (!confirmed) {
                    Swal.showValidationMessage("Please confirm that the information is correct.")
                    return false
                }

                try {
                    const response = await fetch(this.updateUrlValue, {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
                            "Accept": "application/json"
                        },
                        body: JSON.stringify({
                            first_name: firstName,
                            last_name: lastName,
                            address: address,
                            city: city,
                            province_state_id: provinceStateId,
                            postal: postal,
                            country_id: countryId
                        })
                    })

                    const data = await response.json()

                    if (!response.ok || !data.success) {
                        throw new Error(data.error || "Something went wrong.")
                    }

                    return true
                } catch (error) {
                    Swal.showValidationMessage(error.message)
                    return false
                }
            }
        })

        if (result.isConfirmed) {
            let timerInterval

            await Swal.fire({
                icon: "success",
                iconColor: "#16a34a",
                title: "Thanks for confirming!",
                html: `
                Your contact details have been saved.
                <br><br>
                <span class="small text-muted">
                  Closing in <b>4</b> seconds...
                </span>
            `,
                timer: 4000,
                timerProgressBar: true,
                showConfirmButton: false,
                allowOutsideClick: () => !Swal.isLoading(),
                showLoaderOnConfirm: true,
                showClass: {
                    popup: "animate__animated animate__fadeInUp animate__faster"
                },
                hideClass: {
                    popup: "animate__animated animate__fadeOut animate__faster"
                },
                customClass: {
                    popup: "rounded-4"
                },
                didOpen: () => {
                    const b = Swal.getHtmlContainer().querySelector("b")
                    timerInterval = setInterval(() => {
                        b.textContent = Math.ceil(Swal.getTimerLeft() / 1000)
                    }, 100)
                },
                willClose: () => {
                    clearInterval(timerInterval)
                }
            })
        }

        if (result.dismiss === Swal.DismissReason.cancel) {
            try {
                await fetch(this.dismissUrlValue, {
                    method: "POST",
                    headers: {
                        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
                        "Accept": "application/json"
                    }
                })
            } catch (error) {
                console.error("Could not dismiss host contact prompt:", error)
            }
        }
    }

    escape(value) {
        return (value || "")
            .replace(/&/g, "&amp;")
            .replace(/"/g, "&quot;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
    }
}